import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import type { OnApproveData, PayPalScriptOptions } from '@paypal/paypal-js';
import { Theme } from '@pmi.web/react-theme';
import { PayPalCheckoutCreatePaymentOptions } from 'braintree-web';
import {
  PayPalCheckout,
  PayPalCheckoutTokenizationOptions,
} from 'braintree-web/paypal-checkout';

import { registerStartupable } from '@/services/base/StartupHelper';
import { isProdBuild } from '@/utils/env';

import { BraintreeClient } from './BraintreeClient';
import { OptionalStartupable, StartupAuthContext } from '../base/Startupable';
import PaymentStore from '../payments/PaymentStore';
import { PayPalIdAlreadyUsedError } from './errors/PayPalIdAlreadyUsedError';
import { PayPalCheckoutInstanceMissingError } from './errors/PayPalInstanceMissingError';
import { createPayPalCheckoutInstance } from './helpers/BraintreeSdkWrapper';
import { PayPalSupportedLocale } from './helpers/BraintreeSupportedLocalesHelper';
import { PayPalBtnStyle } from './schemas/PayPalBtnStyle';

class BraintreePayPalService implements OptionalStartupable {
  public readonly name = 'BraintreePayPalService';
  public readonly moduleId = PaymentModuleId.BraintreePayPal;

  private _checkoutInstance: PayPalCheckout | undefined;
  private _braintreeClient!: BraintreeClient;

  constructor() {
    registerStartupable(this, [PaymentStore]);
  }

  public async startup(
    _mode: unknown,
    authContext?: StartupAuthContext,
  ): Promise<void> {
    if (authContext === undefined) {
      throw new Error('Auth context is not set');
    }

    this.ensureCanInit();

    const paypalToken = await PaymentStore.getBraintreePaypalToken();
    if (paypalToken === undefined) {
      throw new Error('Paypal token has no value!');
    }

    this._braintreeClient = new BraintreeClient(
      this.moduleId,
      paypalToken,
      authContext.accessToken,
    );
    await this._braintreeClient.init();

    this._checkoutInstance = await createPayPalCheckoutInstance(
      this._braintreeClient.client,
    );
  }

  public shouldStart(): boolean {
    const isAllowed = PaymentStore.allowedPaymentModules.includes(
      this.moduleId,
    );

    const hasAtLeastOneInstrument =
      PaymentStore.instruments.find((i) => i.moduleId === this.moduleId) !==
      undefined;

    return isAllowed || hasAtLeastOneInstrument;
  }

  public createBillingAgreement(locale: SupportedLanguage) {
    if (!this._checkoutInstance) {
      throw new PayPalCheckoutInstanceMissingError();
    }

    const braintreeLocale = PayPalSupportedLocale[locale];
    const options: PayPalCheckoutCreatePaymentOptions = {
      enableShippingAddress: false,
      shippingAddressEditable: false,
      flow: 'vault' as never, // The enum is not exported by the dependency
      intent: 'capture' as never, // The enum is not exported by the dependency
      vaultInitiatedCheckoutPaymentMethodToken: braintreeLocale,
      locale: braintreeLocale,
      currency: PaymentStore.currency,
      amount: PaymentStore.orderTotal,
    };

    return this._checkoutInstance.createPayment(options);
  }

  public async getTokenizedPayloadNonce(data: OnApproveData) {
    if (!this._checkoutInstance) {
      throw new PayPalCheckoutInstanceMissingError();
    }

    const tokenizeOptions: PayPalCheckoutTokenizationOptions = {
      payerId: data.payerID ?? '',
      billingToken: data.billingToken ?? '',
      paymentId: data.paymentID ?? '',
      vault: true,
    };

    const tokenizedData = await this._checkoutInstance.tokenizePayment(
      tokenizeOptions,
    );

    return tokenizedData.nonce;
  }

  public getPayPalBtnStyle(theme: Theme): PayPalBtnStyle {
    return {
      shape: 'pill',
      label: 'paypal',
      layout: 'horizontal',
      tagline: false,
      height: 55,
      color: theme === Theme.FitLine ? 'gold' : 'blue',
    };
  }

  public getPayPalScriptOptions(
    locale: SupportedLanguage,
  ): PayPalScriptOptions {
    return {
      clientId: import.meta.env.VITE_PAYPAL_SDK_CLIENTID ?? '',
      disableFunding: ['card', 'paylater'],
      vault: true,
      intent: 'tokenize',
      locale: PayPalSupportedLocale[locale],
      components: 'buttons',
      debug: !isProdBuild,
    };
  }

  public authorize(nonceToken?: string, instrumentId?: string) {
    return this._braintreeClient.authorize(
      PaymentStore.paymentIntentId,
      nonceToken,
      instrumentId,
    );
  }

  public async tokenize(
    nonceToken: string,
    customerNumber: string | undefined,
  ) {
    if (customerNumber === undefined) {
      throw new Error('Customer number is not set');
    }

    if (PaymentStore.legalEntity === undefined) {
      throw new Error('Legal entity cannot be undefined');
    }

    await this._braintreeClient.tokenize(
      PaymentStore.legalEntity,
      customerNumber,
      nonceToken,
    );

    return Promise.resolve();
  }

  private ensureCanInit() {
    const domElement = document.getElementById('paypal') ?? undefined;

    if (domElement !== undefined) {
      throw new PayPalIdAlreadyUsedError();
    }
  }
}

export default new BraintreePayPalService();
